import React, { useMemo, useState } from 'react';
import { CameraIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { API_URL } from '../../api/config';

export interface EvidenceGroup {
    label: string;
    urls?: string[];
}

interface EvidenceGalleryProps {
    groups: EvidenceGroup[];
}

export const resolveMediaUrl = (url?: string): string | null => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `${API_URL}/${String(url).replace(/^\//, '')}`;
};

const toArray = (value: any): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
};

/**
 * Photo evidence grouped by stage (before / during / after / additional).
 * Clicking a thumbnail opens a full-size lightbox.
 */
const EvidenceGallery: React.FC<EvidenceGalleryProps> = ({ groups }) => {
    const items = useMemo(
        () =>
            groups.flatMap((group) =>
                toArray(group.urls).map((url) => ({ group: group.label, url: resolveMediaUrl(url) as string })),
            ),
        [groups],
    );
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
    const totalPhotos = items.length;

    const move = (direction: 1 | -1) => {
        setLightboxIndex((current) => {
            if (current === null) return current;
            const next = current + direction;
            if (next < 0 || next >= totalPhotos) return current;
            return next;
        });
    };

    const rendered = groups.filter((group) => toArray(group.urls).length > 0);

    return (
        <div className="space-y-4">
            {rendered.length === 0 ? (
                <div className="flex items-center gap-2 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-400">
                    <CameraIcon className="h-4 w-4" />
                    No photo evidence attached.
                </div>
            ) : (
                rendered.map((group) => {
                    const urls = toArray(group.urls);
                    return (
                        <div key={group.label}>
                            <p className="mb-2 text-xs font-black uppercase tracking-wide text-slate-500">
                                {group.label}
                                <span className="ml-1.5 font-bold text-slate-400">({urls.length})</span>
                            </p>
                            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                                {urls.map((url, index) => {
                                    const absolute = resolveMediaUrl(url) as string;
                                    const photoIndex = items.findIndex((i) => i.url === absolute);
                                    return (
                                        <button
                                            key={`${group.label}-${index}`}
                                            type="button"
                                            onClick={() => setLightboxIndex(photoIndex)}
                                            className="group relative aspect-square overflow-hidden rounded-lg border border-slate-200 bg-slate-100"
                                        >
                                            <img
                                                src={absolute}
                                                alt={`${group.label} ${index + 1}`}
                                                loading="lazy"
                                                className="h-full w-full object-cover transition duration-200 group-hover:scale-105"
                                                onError={(e) => {
                                                    e.currentTarget.style.display = 'none';
                                                }}
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })
            )}

            {lightboxIndex !== null && items[lightboxIndex] && (
                <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-slate-950/90 p-4" onClick={() => setLightboxIndex(null)}>
                    <button
                        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                    <div className="max-h-[85vh] max-w-4xl" onClick={(e) => e.stopPropagation()}>
                        <img src={items[lightboxIndex].url} alt="" className="max-h-[80vh] w-auto rounded-xl shadow-2xl" />
                        <p className="mt-2 text-center text-xs font-bold text-slate-300">
                            {items[lightboxIndex].group} · {lightboxIndex + 1} of {totalPhotos}
                        </p>
                        <div className="mt-3 flex items-center justify-center gap-3">
                            <button
                                onClick={() => move(-1)}
                                disabled={lightboxIndex === 0}
                                className="rounded-lg bg-white/10 px-4 py-1.5 text-sm font-bold text-white disabled:opacity-40"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => move(1)}
                                disabled={lightboxIndex === totalPhotos - 1}
                                className="rounded-lg bg-white/10 px-4 py-1.5 text-sm font-bold text-white disabled:opacity-40"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EvidenceGallery;
